import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import dynamic from 'next/dynamic';
// Lib
import * as ga from 'lib/google-analytics';
import * as ttq from 'lib/ttpixel';
import * as fbq from 'lib/fpixel';
// Hooks
import { useGetCurrUser, useAppContext } from 'hooks';
// Api
import { ADD_MERCH_ITEM_TO_CART } from 'api/merch/mutations';
import { GET_MERCH_CART_ITEM_LIST } from 'api/merch/queries';
import { ME } from 'api/auth/queries';
// Types
import {
  AddMerchItemToCart,
  AddMerchItemToCartVariables,
} from 'api/merch/types/AddMerchItemToCart';
import { AuctionStatus, MemorabiliaStatus } from 'api/graphql-global-types';
import {
  GetMemorabiliaBySlug,
  GetMemorabiliaBySlugVariables,
  GetMemorabiliaBySlug_getMemorabiliaBySlug,
} from 'api/memorabilia/types/GetMemorabiliaBySlug';
// Helpers
import { formatCurrencyString } from 'helpers/formatCurrencyString';
import { getEnvLink } from 'helpers/routes';
// Hooks
import { useRefetchOnUserLogIn } from 'hooks';
// Ui
import Button from 'ui3/Button/Button';
import HashTags from 'ui3/HashTags/HashTags';
import Text from 'ui3/Text/Text';
// Components
import QuantityAndPrice from 'components/common3/QuantityAndPrice/QuantityAndPrice';
import ProductDetailsAccordion from 'components/common3/ProductDetailsAccordion/ProductDetailsAccordion';
import ProductImages, {
  ProductImageType,
} from 'components/common3/ProductImages/ProductImages';
import { showToast } from 'components/common/Toast/Toast';
import SecurePayments from 'components/common3/EcommerceTrustBadges/SecurePayments/SecurePayments';
import DetailsAndPolicies from 'components/common3/DetailsAndPolicies/DetailsAndPolicies';
import RelatedProductsList from 'components/Athlete/components/RelatedProductsList/RelatedProductsList';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
import TalentBlock from 'components/common3/TalentBlock/TalentBlock';
import AuctionCountdown from './components/AuctionCountdown/AuctionCountdown';
// Styles
import styles from './Memorabilia.module.scss';
// Dynamic imports
const PlaceBidForm = dynamic(
  () => import('./components/PlaceBidForm/PlaceBidForm'),
  { ssr: false }
);
const Modal = dynamic(() => import('../../ui3/Modal/Modal'), { ssr: false });
const StripeElements = dynamic(
  () => import('../../layouts/StripeElements/StripeElements'),
  { ssr: false }
);

type MemorabiliaProps = {
  memorabilia?: GetMemorabiliaBySlug_getMemorabiliaBySlug;
  refetch: (
    variables?: Partial<GetMemorabiliaBySlugVariables> | undefined
  ) => Promise<ApolloQueryResult<GetMemorabiliaBySlug>>;
};

const Memorabilia = ({ memorabilia, refetch }: MemorabiliaProps) => {
  const { data: userData, updateQuery } = useGetCurrUser();
  const isLogged = !!userData?.me;
  const router = useRouter();

  const {
    guestToken,
    addMerchItemToShoppingCart,
    setLoginModalVisibility,
  } = useAppContext();

  useRefetchOnUserLogIn({ refetch });

  const [
    addMerchItemToCart,
    { loading: addMerchItemToCartLoading },
  ] = useMutation<AddMerchItemToCart, AddMerchItemToCartVariables>(
    ADD_MERCH_ITEM_TO_CART
  );

  const isActive = memorabilia?.status === MemorabiliaStatus.Active;
  const productType = memorabilia?.memorabiliaProductType;
  const memorabiliaName = memorabilia?.title || '';
  const auction = memorabilia?.auctions?.[0];
  const isForAuction = memorabilia?.isForAuction;
  const isAuctionLive = auction?.status === AuctionStatus.Live;
  const isAuctionScheduled = auction?.status === AuctionStatus.Scheduled;
  const isAuctionFinished = auction?.status === AuctionStatus.Finished;
  const userHasBid = Boolean(
    isForAuction &&
      auction?.reservedBids.find((bid) => bid.customerId === userData?.me.id)
  );
  const isUserWinning = Boolean(
    userHasBid && auction?.reservedBids[0]?.customerId === userData?.me.id
  );

  const [amount, setAmount] = useState<number>(1);

  const [isPlaceBidVisible, setIsPlaceBidVisible] = useState<boolean>(false);

  const [
    addToCartLoadingSimulation,
    setAddToCartLoadingSimulation,
  ] = useState<boolean>(false);

  const handleAddToCartClick = async () => {
    const productPrice = (amount * (memorabilia?.price || 0)).toFixed(2);

    const currency = 'USD';

    if (isLogged || guestToken) {
      try {
        await addMerchItemToCart({
          variables: {
            input: {
              items: [
                {
                  memorabiliaId: memorabilia?.id,
                  amount,
                },
              ],
            },
          },
          onCompleted: (data) => {
            updateQuery((prevUserData) => ({
              ...prevUserData,
              me: {
                ...prevUserData.me,
                cartSize: data.addMerchItemToCart.length,
              },
            }));
          },
          ...(isLogged && {
            refetchQueries: [{ query: ME }],
          }),
          ...(guestToken && {
            refetchQueries: [{ query: GET_MERCH_CART_ITEM_LIST }],
          }),
        });

        if (guestToken && memorabilia) {
          addMerchItemToShoppingCart({
            memorabilia: memorabilia,
            memorabiliaId: memorabilia.id,
            amount,
          });
        }

        showToast({
          message: 'Product successfully added to cart',
        });
      } catch (error) {
        showToast({
          message: error?.message,
          type: 'error',
        });
      }
    } else {
      if (memorabilia) {
        addMerchItemToShoppingCart({
          memorabilia: memorabilia,
          memorabiliaId: memorabilia.id,
          amount,
        });

        fbq.event('AddToCart', {
          value: productPrice,
          currency: 'USD',
          contents: [
            {
              id: memorabilia?.id,
              quantity: amount,
              price: productPrice,
            },
          ],
          content_type: 'product',
          content_ids: [memorabilia?.id],
        });

        setAddToCartLoadingSimulation(true);

        setTimeout(() => {
          setAddToCartLoadingSimulation(false);

          showToast({
            message: 'Product successfully added to cart',
          });
        }, 500);
      }
    }

    ga.addToCart({
      currency,
      value: Number(productPrice || 0),
      items: [
        {
          id: memorabilia?.id || '',
          name: memorabilia?.title || '',
          category: productType,
          brand: memorabilia?.store?.storeDetails?.storeName || '',
          quantity: amount,
          price: Number(memorabilia?.price),
        },
      ],
    });

    ttq.addToCart({
      content_id: memorabilia?.id || '',
      content_type: 'product',
      content_name: memorabilia?.title,
      content_category: productType,
      quantity: +amount,
      price: Number(memorabilia?.price),
      value: Number(productPrice || 0),
      currency,
    });
  };

  const handleOnPlaceBidClick = () => {
    if (!isLogged) {
      setLoginModalVisibility(true);
    } else {
      setIsPlaceBidVisible(true);
    }
  };

  const onPlaceBidClose = () => {
    setIsPlaceBidVisible(false);
  };

  const onPlaceBidSuccess = () => {
    refetch();
    setIsPlaceBidVisible(false);
  };

  const handleAmountChange = (amount: number) => {
    setAmount(amount);
  };

  const amountPrice = amount
    ? Number(memorabilia?.price || 0) * amount
    : Number(memorabilia?.price || 0);

  const totalPrice = formatCurrencyString(
    isForAuction ? memorabilia.auctions[0].currentPrice : amountPrice
  );
  const addToCartLoading =
    addMerchItemToCartLoading || addToCartLoadingSimulation;

  const store = memorabilia?.store;
  const storeName = store?.storeDetails?.storeName || '';
  const profilePath = store?.slug ? `/${store.slug}` : '#';
  const avatarURL = store?.storeDetails?.avatarURL || '';
  const sharePath = profilePath !== '#' ? `${getEnvLink()}${profilePath}` : '#';
  const isUserOwner = userData?.me?.id === store?.id;

  const isAddToCartDisabled =
    memorabilia && memorabilia?.numberOfUnits < Number(amount);

  const productImages: ProductImageType[] =
    memorabilia?.images?.map((image) => ({
      id: `${image.id}`,
      isMainImage: Boolean(image.isMainImage),
      imageUrl: image.url,
    })) ?? [];

  const renderPriceLabel = () => {
    if (!isForAuction) {
      return '';
    }

    if (
      isAuctionScheduled ||
      (isAuctionLive && auction.reservedBids.length === 0)
    ) {
      return 'Starting Price';
    }

    if (isAuctionLive) {
      return 'Current Highest Bid';
    }

    if (isAuctionFinished) {
      return 'Sold at';
    }

    return '';
  };

  const renderActionButton = () => {
    if (!isForAuction) {
      return (
        <Button
          variant="primary"
          onClick={handleAddToCartClick}
          loading={addToCartLoading}
          className={styles.primaryButton}
          disabled={isAddToCartDisabled}
          icon="shoppingCart"
        >
          Add To Cart
        </Button>
      );
    }

    if (isAuctionLive) {
      return (
        <Button
          variant="primary"
          onClick={handleOnPlaceBidClick}
          loading={addToCartLoading}
          className={styles.primaryButton}
          disabled={isUserWinning}
          icon="curvedDollar"
        >
          Place Bid
        </Button>
      );
    }

    // Auction not yet started (scheduled) or no auction record yet: show disabled Place Bid
    if (isForAuction) {
      return (
        <Button
          variant="primary"
          className={styles.primaryButton}
          disabled
          icon="curvedDollar"
        >
          Place Bid
        </Button>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!isActive && memorabilia?.store?.slug) {
      router.push(`/${memorabilia.store.slug}/memorabilia`);
    }
  }, [memorabilia, isActive, router]);

  const hasMoreThanOne = memorabilia && memorabilia.numberOfUnits > 1;
  const showAllLink = memorabilia?.store?.slug
    ? `/${memorabilia.store.slug}/memorabilia`
    : '';

  return (
    <div className={styles.root}>
      <div className={styles.memorabiliaContainer}>
        <section className={styles.imageContainer}>
          {store && (
            <div className={styles.mobileView}>
              <TalentBlock
                store={store}
                talentName={storeName}
                avatarURL={avatarURL}
                profilePath={profilePath}
                showFollowButton={!isUserOwner}
                className={styles.talentBlock}
              />
            </div>
          )}
          <div className={styles.availableUnitsWrapper}>
            {hasMoreThanOne && (
              <div className={styles.numberAvailable}>
                <Text
                  variant="captionRegular"
                  className={styles.numberOfUnitsText}
                >{`only ${memorabilia?.numberOfUnits} available`}</Text>
              </div>
            )}
          </div>

          <ProductImages images={productImages} productName={memorabiliaName} />
        </section>

        <section className={styles.productSection}>
          {store && (
            <div className={styles.desktopView}>
              <TalentBlock
                store={store}
                talentName={storeName}
                avatarURL={avatarURL}
                profilePath={profilePath}
                showFollowButton={!isUserOwner}
                className={styles.talentBlock}
              />
            </div>
          )}

          <Text as="h1" variant="h5" className={styles.title}>
            {memorabiliaName}
          </Text>

          <HashTags
            variant="secondary"
            tags={memorabilia?.hashtags || []}
            className={styles.hashTags}
          />

          {isForAuction && (
            <div className={styles.auctionPriceBlock}>
              <div className={styles.priceBlock}>
                {renderPriceLabel() && (
                  <Text variant="subtitle2" className={styles.priceLabel}>
                    {renderPriceLabel()}
                  </Text>
                )}
                <Text variant="h5" className={styles.priceValue}>
                  {totalPrice}
                </Text>
              </div>
              {memorabilia && auction && (
                <AuctionCountdown
                  status={auction.status}
                  date={
                    isAuctionScheduled
                      ? memorabilia.onAuctionFrom
                      : memorabilia.onAuctionTo
                  }
                />
              )}
            </div>
          )}

          {!isForAuction && (
            <QuantityAndPrice
              maxQuantity={memorabilia?.numberOfUnits || 99}
              value={amount}
              onChange={handleAmountChange}
              totalPrice={totalPrice}
              showQuantitySelector={!!hasMoreThanOne}
              classNames={{
                wrapper: styles.quantityAndPriceWrapper,
                quantityAndPrice: styles.quantityAndPrice,
                quantity: styles.quantity,
                priceValue: styles.priceValue,
              }}
            />
          )}

          <div className={styles.actionButtonWrapper}>
            <div className={styles.actionButtonInner}>
              {renderActionButton()}
            </div>
            <SocialShareQRButton
              modalTitle="Share"
              modalText="Share this with your friends"
              path={sharePath}
              twitterText={`Check this out on @millionsdotco`}
              icon
              onlyIcon
            />
          </div>

          {memorabilia && auction && (
            <div className={styles.memorabiliaAuctionInfo}>
              {!isAuctionFinished && isUserWinning && (
                <Text variant="subtitle2">Your bid is winning!</Text>
              )}
              {!isAuctionFinished && userHasBid && !isUserWinning && (
                <Text variant="subtitle2">
                  Someone placed higher bid than you! But you can place a new
                  bid to take a lead again.
                </Text>
              )}
              {isAuctionLive && (
                <Text variant="subtitle2" className={styles.numberOfBids}>
                  {auction.reservedBids.length} active{' '}
                  {auction.reservedBids.length > 1 ? 'bids' : 'bid'}
                </Text>
              )}
            </div>
          )}

          <SecurePayments />

          <div className={styles.productDetailsSection}>
            {memorabilia?.description && (
              <ProductDetailsAccordion
                description={memorabilia.description}
                openedByDefault={false}
              />
            )}

            <DetailsAndPolicies showShipping showMemorabilia />
          </div>
        </section>
      </div>

      <RelatedProductsList
        storeId={memorabilia?.store?.id || ''}
        currentId={memorabilia?.id}
        title="You May Also Like"
        seeAllLink={showAllLink}
        isProductDetailsPage
        showOnlyMemorabilia
      />

      {isLogged && auction && isAuctionLive && (
        <StripeElements>
          <Modal
            open={isPlaceBidVisible}
            title="Place Bid"
            onClose={onPlaceBidClose}
            size="medium"
          >
            <PlaceBidForm
              auction={auction}
              onSuccess={onPlaceBidSuccess}
              onRefresh={refetch}
            />
          </Modal>
        </StripeElements>
      )}
    </div>
  );
};

export default Memorabilia;
